import { Edge, Node } from '@xyflow/react';
import {
  EDataType,
  EDataSwitchType,
  NodeCategoryModel,
  IDhcpServerSettings,
  IStpValidationErrors,
  INodeValidationErrors,
  IDhcpServerValidationErrors,
} from 'src/modules/network-configuration/models/node-data';
import { NodeModel } from 'src/modules/network-configuration/models/node-model';
import { EEvents } from 'src/types/events';

import {
  isV4Format,
  isValidIPAddress,
  isValidSubnetMask,
  isValidHostAddress,
  isWhitelistedSubnet,
  isValidSubnetAddress,
  calculateNetworkAddress2,
} from './ip';
// eslint-disable-next-line import/no-cycle
import { getAllNodeConnected } from './node';

export function validateIp(ip: string) {
  return /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
    ip,
  );
}

export function allElementsAreEqual(arr: any[]) {
  return arr.length === 0 || arr.every((item) => item === arr[0]);
}

export function allElementsValid(arr: any[]) {
  return arr.every(
    (item) => item !== null && item !== undefined && item !== '',
  );
}

export type IValidateNodeResult = {
  isValid: boolean;
  errorMessage: INodeValidationErrors;
};

export type INodeErrorMessage = INodeValidationErrors;

export const validateNode = (
  id: string,
  newData: NodeCategoryModel,
  nodes: Node<NodeModel>[],
  edges: Edge[],
  globalIpSubnet: string,
  isSilent = true,
  assignedSubnetMask?: string,
): IValidateNodeResult => {
  const newErrors: INodeValidationErrors = {
    name: '',
    ip: '',
    subnet: '',
    subnetMask: '',
    gateway: '',
    ipInterfaces: [],
    stp: {},
    dhcpServer: {},
  };

  if (!newData) return { isValid: false, errorMessage: newErrors };

  if (!newData.name) {
    newErrors.name = 'Name is required';
  } else if (newData.type === EDataType.Device) {
    newErrors.name = validateNameDevice(newData.name);
  } else if (newData.type === EDataType.Switch) {
    newErrors.name = validateNameSwitch(newData.name);
  }

  // check duplicate name
  const duplicateName = nodes.find(
    (node) =>
      (node.data?.category as NodeCategoryModel)?.name === newData.name &&
      node.id !== id,
  );
  if (duplicateName) {
    newErrors.name = 'Duplicate Name';
  }
  const interfaceErrors: INodeValidationErrors['ipInterfaces'] = [];

  const isManaged =
    newData.type === EDataType.Device ||
    newData.switchType?.includes(EDataSwitchType.MANAGED);

  const connectedNodes = getAllNodeConnected(id, edges, nodes);

  if (isManaged) {
    if (!newData.ipInterfaces || newData.ipInterfaces.length === 0) {
      const subnet =
        globalIpSubnet ||
        calculateNetworkAddress2(newData.ip || '', newData.subnetMask || '');

      const maskForSubnetCheck =
        globalIpSubnet && assignedSubnetMask
          ? assignedSubnetMask
          : newData.subnetMask || '';

      const validateIpSubnetResult = getErrorIpSubnet({
        ip: newData.ip || '',
        subnet,
        subnetMask: maskForSubnetCheck,
      });

      newErrors.ip = validateIpSubnetResult.ip;
      newErrors.subnet = validateIpSubnetResult.subnet;
      newErrors.subnetMask = validateIpSubnetResult.subnetMask;

      // check duplicate IP
      if (!newErrors.ip) {
        const duplicateIp = nodes.find(
          (node) =>
            (node.data?.category as NodeCategoryModel)?.ip === newData.ip &&
            node.id !== id,
        );

        if (duplicateIp) {
          newErrors.ip = 'Duplicate IP';
        }
      }
    } else if (newData.ipInterfaces.length > 0) {
      const allIpInterfaces = connectedNodes
        .map(
          (node) =>
            (node.data?.category as NodeCategoryModel)?.ipInterfaces || [],
        )
        .flat();

      newData.ipInterfaces.forEach((ipInterface) => {
        const allIpInterfacesWithSameVlanId = allIpInterfaces.filter(
          (ip) => ip.vlanId.toString() === ipInterface.vlanId.toString(),
        );

        const isSomeDeviceDhcp = allIpInterfacesWithSameVlanId.some(
          (ip) => ip.dhcp,
        );

        let interfaceError = {
          ip: '',
          subnet: '',
          subnetMask: '',
        };
        if (isSomeDeviceDhcp || ipInterface.ip) {
          interfaceError = getErrorIpSubnet({
            ip: ipInterface.ip || '',
            subnet: ipInterface.subnet || '',
            subnetMask: ipInterface.subnetMask || '',
          });

          // check duplicate ip address
          if (!interfaceError.ip) {
            const duplicateIp = nodes
              .filter((node) => node.id !== id)
              .find((node) =>
                (node.data?.category as NodeCategoryModel)?.ipInterfaces?.some(
                  (ip) => ip.ip === ipInterface.ip,
                ),
              );

            if (duplicateIp) {
              interfaceError.ip = 'Duplicate IP';
            }
          }
        }

        interfaceErrors.push(interfaceError);
      });
    }
  }

  const isManagedSwitch = newData.switchType?.includes(EDataSwitchType.MANAGED);
  if (isManagedSwitch) {
    const gateway = newData.gateway || '';
    // Treat a value consisting only of dots (empty IPInput) as unset
    const isGatewaySet = gateway && gateway.replace(/\./g, '').length > 0;
    if (isGatewaySet) {
      if (!isV4Format(gateway)) {
        newErrors.gateway = 'Invalid gateway IP';
      } else {
        const subnets =
          // eslint-disable-next-line no-nested-ternary
          newData.ipInterfaces && newData.ipInterfaces.length > 0
            ? newData.ipInterfaces
                .filter((iface) => iface.ip && iface.subnetMask)
                .map((iface) => ({
                  network: calculateNetworkAddress2(iface.ip, iface.subnetMask),
                  mask: iface.subnetMask,
                }))
            : newData.ip && newData.subnetMask
              ? [
                  {
                    network: calculateNetworkAddress2(
                      newData.ip,
                      newData.subnetMask,
                    ),
                    mask: newData.subnetMask,
                  },
                ]
              : [];

        const isValidHost = subnets.some(({ network, mask }) =>
          isValidHostAddress(gateway, network, mask),
        );
        if (!isValidHost) {
          newErrors.gateway =
            "Gateway must be a valid host IP within one of the device's IP subnets";
        }
      }
    }
  }

  if (isManagedSwitch && newData.stp) {
    const stpErrors = validateSTP(newData.stp);
    if (Object.keys(stpErrors).length > 0) {
      newErrors.stp = stpErrors;
    }
  }

  if (isManagedSwitch && newData.dhcpServer) {
    let subnet: { subnet: string; subnetMask: string } | undefined;
    if (
      newData.ipInterfaces?.length &&
      newData.ipInterfaces[0].subnet &&
      newData.ipInterfaces[0].subnetMask
    ) {
      subnet = {
        subnet: newData.ipInterfaces[0].subnet,
        subnetMask: newData.ipInterfaces[0].subnetMask,
      };
    } else if (newData.ip && newData.subnetMask) {
      subnet = {
        subnet: calculateNetworkAddress2(newData.ip, newData.subnetMask),
        subnetMask: newData.subnetMask,
      };
    }
    const dhcpErrors = validateDhcpServer(newData.dhcpServer, subnet);
    if (Object.keys(dhcpErrors).length > 0) {
      newErrors.dhcpServer = dhcpErrors;
    }
  }

  const isNodeValid =
    newErrors.name === '' &&
    newErrors.ip === '' &&
    newErrors.subnetMask === '' &&
    newErrors.gateway === '' &&
    interfaceErrors?.every(
      (ip) => ip.ip === '' && ip.subnet === '' && ip.subnetMask === '',
    ) &&
    !(newErrors.stp && Object.keys(newErrors.stp).length > 0) &&
    !(newErrors.dhcpServer && Object.keys(newErrors.dhcpServer).length > 0);
  if (!isSilent) {
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent(EEvents.RELOAD_VALIDATE, {
          detail: {
            nodeIds: connectedNodes.map((node) => node.id),
          },
        }),
      );
    }, 500);
  }

  return {
    isValid: isNodeValid,
    errorMessage: {
      ...newErrors,
      ipInterfaces: interfaceErrors,
      stp: newErrors.stp,
      dhcpServer: newErrors.dhcpServer,
    },
  };
};

function validateSTP(stp: { bridgePriority?: number }): IStpValidationErrors {
  const errors: IStpValidationErrors = {};
  const bp = stp.bridgePriority ?? 8;
  if (bp < 0 || bp > 15) {
    errors.bridgePriority = 'Bridge Priority must be between 0 and 15';
  }
  return errors;
}

export function validateDhcpServer(
  dhcp: IDhcpServerSettings,
  subnetContext?: { subnet: string; subnetMask: string },
): IDhcpServerValidationErrors {
  const errors: IDhcpServerValidationErrors = {};
  if (dhcp.leaseTime != null) {
    if (dhcp.leaseTime < 300) {
      errors.leaseTime = 'Lease Time must be at least 300 seconds';
    } else if (dhcp.leaseTime > 604800) {
      errors.leaseTime = 'Lease Time must be less than 604800 seconds';
    }
  }
  const validateDhcpIp = (ip: string): string => {
    if (!ip || ip === '...') return '';
    if (!isV4Format(ip)) return 'Invalid IP address format';
    if (!isValidIPAddress(ip)) return 'Invalid IP address';
    return '';
  };

  const validatePoolIp = (ip: string): string => {
    const e = validateDhcpIp(ip);
    if (e) return e;
    if (subnetContext && ip) {
      if (
        !isValidHostAddress(ip, subnetContext.subnet, subnetContext.subnetMask)
      ) {
        return 'IP address must be in the chosen subnet';
      }
    }
    return '';
  };
  if (dhcp.poolEnabled) {
    const e1 = validatePoolIp(dhcp.poolStart || '');
    if (e1) errors.poolStart = e1;
    const e2 = validatePoolIp(dhcp.poolEnd || '');
    if (e2) errors.poolEnd = e2;
  }
  const ipFields: (keyof IDhcpServerValidationErrors)[] = [
    'routerIp1',
    'routerIp2',
    'domainServerIp1',
    'domainServerIp2',
    'ntpServerIp1',
    'ntpServerIp2',
  ];
  ipFields.forEach((f) => {
    const v = dhcp[f as keyof IDhcpServerSettings];
    if (v && typeof v === 'string') {
      const e = validateDhcpIp(v);
      if (e) (errors as Record<string, string>)[f] = e;
    }
  });
  if (dhcp.domainName != null && dhcp.domainName.length > 255) {
    errors.domainName = 'Domain must be less than 255 characters';
  }
  return errors;
}

export const getErrorIpSubnet = ({
  ip,
  subnet,
  subnetMask,
  isCheckBroadcastAddress = true,
}: {
  ip: string;
  subnet?: string;
  subnetMask: string;
  isCheckBroadcastAddress?: boolean;
}): {
  ip: string;
  subnet: string;
  subnetMask: string;
} => {
  const result = {
    ip: '',
    subnet: '',
    subnetMask: '',
  };

  if (!ip) {
    result.ip = 'IP is required';
  } else if (!ip || !isV4Format(ip)) {
    result.ip = 'Invalid IP';
  }

  // check if the subnet mask is valid
  if (!subnetMask) {
    result.subnetMask = 'Subnet mask is required';
  } else if (!isValidSubnetMask(subnetMask)) {
    result.subnetMask = 'Invalid subnet mask';
  }

  if (ip && subnetMask) {
    const checkSubnet = calculateNetworkAddress2(ip, subnetMask);

    if (!checkSubnet) {
      result.subnet = 'IP is not part of the assigned subnet';
    }

    if (subnet) {
      if (!isValidSubnetAddress(subnet, subnetMask)) {
        result.subnet = 'IP is not part of the assigned subnet';
      }

      if (subnet !== checkSubnet) {
        result.subnet = 'IP is not part of the assigned subnet';
      }
    }

    if (!isWhitelistedSubnet(checkSubnet)) {
      result.subnet = 'IP range is not reserved for private use';
    }

    if (
      isCheckBroadcastAddress &&
      !isValidHostAddress(ip, checkSubnet, subnetMask)
    ) {
      result.ip = 'Do not use Network address or Broadcast address';
    }
  }

  return result;
};

export const validateNameSwitch = (name: string) => {
  if (!name) {
    return 'Name is required';
  }

  if (name.length > 28) {
    return 'Name must be at most 28 characters';
  }

  if (name.startsWith(' ') || name.endsWith(' ')) {
    return 'Name must not start or end with a space';
  }

  const validCharsRegex = /^[a-zA-Z0-9_ +-]*$/;
  if (!validCharsRegex.test(name)) {
    return 'Name contains invalid characters. Only letters, numbers, spaces, underscores, hyphens, and plus signs are allowed.';
  }

  return '';
};

export const validateNameDevice = (name: string) => {
  if (!name) {
    return 'Name is required';
  }

  if (name.length > 255) {
    return 'Name must be at most 255 characters';
  }

  if (name.startsWith(' ') || name.endsWith(' ')) {
    return 'Name must not start or end with a space';
  }

  return '';
};
